import { useMemo } from "react";
import styles from "./RentGraph.module.css";
// @ts-expect-error pure js
import { classnames } from "../../utils";
// @ts-expect-error pure js
import { currencyFormatter } from "../../api/Functions";
import Text from "./Text";
import { getRecurrence } from "../../utils/rent";

export interface AutreAppel {
  label: string;
  montant: number;
  tva?: boolean;
  remboursement?: boolean;
}

export interface RentInput {
  loyer_hc?: number;
  complement_loyer?: number;
  charges?: number;
  tva?: boolean;
  taux_tva?: number;
  tva_charge?: boolean;
  taux_tva_charge?: number;
  autre_appel?: AutreAppel[];
  payment_recurrence?: number;
}

type SegmentColor =
  | "orange"
  | "orange-dark"
  | "blue"
  | "blue-dark"
  | "violet"
  | "violet-dark"
  | "green"
  | "green-dark"
  | "red"
  | "red-dark";

interface RentSegment {
  value: number;
  label: string;
  color: SegmentColor;
}

const APPEL_COLORS: Array<{ light: SegmentColor; dark: SegmentColor }> = [
  { light: "violet", dark: "violet-dark" },
  { light: "green", dark: "green-dark" },
  { light: "red", dark: "red-dark" },
];

interface RentGraphProps {
  title: string;
  tenant: RentInput;
  className?: string;
}

function calculateRentSegments(tenant: RentInput): RentSegment[] {
  const segments: RentSegment[] = [];

  const loyerHT = tenant.loyer_hc || 0;
  const complementLoyer = tenant.complement_loyer || 0;
  const chargesHT = tenant.charges || 0;
  const tauxLoyer = tenant.tva ? (tenant.taux_tva || 20) / 100 : 0;
  const tauxCharges = tenant.tva_charge
    ? (tenant.taux_tva_charge || tenant.taux_tva || 20) / 100
    : 0;

  const loyerTVA = loyerHT * tauxLoyer;
  const chargesTVA = chargesHT * tauxCharges;

  if (loyerHT > 0) {
    segments.push({
      value: loyerHT,
      label: "Loyer hors charges",
      color: "orange",
    });
  }

  if (complementLoyer > 0) {
    segments.push({
      value: complementLoyer,
      label: "Complément de loyer",
      color: "orange-dark",
    });
  }

  if (loyerTVA > 0) {
    segments.push({
      value: loyerTVA,
      label: "TVA loyer",
      color: "orange-dark",
    });
  }

  if (chargesHT > 0) {
    segments.push({
      value: chargesHT,
      label: "Charges",
      color: "blue",
    });
  }

  if (chargesTVA > 0) {
    segments.push({
      value: chargesTVA,
      label: "TVA charges",
      color: "blue-dark",
    });
  }

  if (Array.isArray(tenant.autre_appel)) {
    tenant.autre_appel.forEach((appel, index) => {
      if (appel.montant > 0 && !appel.remboursement) {
        const tauxAppel = appel.tva ? (tenant.taux_tva || 20) / 100 : 0;
        const appelTVA = appel.montant * tauxAppel;
        const colorPair = APPEL_COLORS[index % APPEL_COLORS.length];

        segments.push({
          value: appel.montant,
          label: appel.label,
          color: colorPair.light,
        });

        if (appelTVA > 0) {
          segments.push({
            value: appelTVA,
            label: `TVA ${appel.label}`,
            color: colorPair.dark,
          });
        }
      }
    });
  }

  return segments;
}

// Les remboursements ne peuvent pas être des segments (barre empilée) : ils sont
// déduits du total affiché.
function calculateRemboursements(tenant: RentInput): number {
  if (!Array.isArray(tenant.autre_appel)) {
    return 0;
  }
  return tenant.autre_appel.reduce((sum, appel) => {
    if (!appel.remboursement) {
      return sum;
    }
    const tauxAppel = appel.tva ? (tenant.taux_tva || 20) / 100 : 0;
    return sum + Math.abs(appel.montant || 0) * (1 + tauxAppel);
  }, 0);
}

export default function RentGraph({
  title,
  tenant,
  className,
}: RentGraphProps) {
  const segments = useMemo(() => calculateRentSegments(tenant), [tenant]);
  const totalValue = useMemo(
    () =>
      segments.reduce((sum, seg) => sum + seg.value, 0) -
      calculateRemboursements(tenant),
    [segments, tenant],
  );

  if (segments.length === 0) {
    return null;
  }

  return (
    <div className={className}>
      <div className={styles.header}>
        <Text color="muted">{title}</Text>
        <div>
          <Text variant="kilo" weight="medium" as="span">
            {currencyFormatter(totalValue)}
          </Text>{" "}
          <Text variant="deci" as="span" color="muted">
            {getRecurrence(tenant.payment_recurrence)}
          </Text>
        </div>
      </div>

      <div className={styles.barContainer}>
        {segments.map((segment, index) => {
          const percentage =
            totalValue > 0 ? (segment.value / totalValue) * 100 : 0;

          return (
            <div
              key={index}
              className={classnames(styles.barSegment, styles[segment.color])}
              style={{ width: `${percentage}%` }}
            />
          );
        })}
      </div>

      <div className={styles.legend}>
        {segments.map((segment, index) => (
          <div key={index} className={styles.legendItem}>
            <div
              className={classnames(styles.legendDot, styles[segment.color])}
            />
            <Text
              variant="deci"
              weight="medium"
              as="span"
              className={styles.legendValue}
            >
              {currencyFormatter(segment.value)}
            </Text>
            <Text variant="deci" as="span" color="muted">
              {segment.label}
            </Text>
          </div>
        ))}
      </div>
    </div>
  );
}
